import React, { Suspense, useEffect, useRef, useState, useMemo } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import {
  useGLTF,
  useTexture,
  Loader,
  Environment,
  useFBX,
  useAnimations,
  OrthographicCamera,
} from "@react-three/drei";
import { MeshStandardMaterial } from "three/src/materials/MeshStandardMaterial";

import { LinearEncoding, sRGBEncoding } from "three/src/constants";
import { LineBasicMaterial, MeshPhysicalMaterial, Vector2 } from "three";
import ReactAudioPlayer from "react-audio-player";

import createAnimation from "./converter";
import blinkData from "./blendDataBlink.json";

import * as THREE from "three";
import axios from "axios";

import queryString from "query-string";

const _ = require("lodash");

const host = "https://talkingapi.vplaysmart.com";

let SpeechRecognition =
  window.webkitSpeechRecognition || window.SpeechRecognition;
let SpeechGrammarList =
  window.SpeechGrammarList || window.webkitSpeechGrammarList;
let correct_count = 0;
let wrong_count = 0;

function Avatar({
  avatar_url,
  speak,
  setSpeak,
  text,
  setAudioSource,
  playing,
  mixerRef,
  recognitionRef, // Add this prop
}) {
  let gltf = useGLTF(avatar_url);
  let morphTargetDictionaryBody = null;
  let morphTargetDictionaryLowerTeeth = null;

  const [
    bodyTexture,
    eyesTexture,
    teethTexture,
    bodySpecularTexture,
    bodyRoughnessTexture,
    bodyNormalTexture,
    teethNormalTexture,
    // teethSpecularTexture,
    hairTexture,
    tshirtDiffuseTexture,
    tshirtNormalTexture,
    tshirtRoughnessTexture,
    hairAlphaTexture,
    hairNormalTexture,
    hairRoughnessTexture,
  ] = useTexture([
    "/images/body.webp",
    "/images/eyes.webp",
    "/images/teeth_diffuse.webp",
    "/images/body_specular.webp",
    "/images/body_roughness.webp",
    "/images/body_normal.webp",
    "/images/teeth_normal.webp",
    // "/images/teeth_specular.webp",
    "/images/h_color.webp",
    "/images/tshirt_diffuse.webp",
    "/images/tshirt_normal.webp",
    "/images/tshirt_roughness.webp",
    "/images/h_alpha.webp",
    "/images/h_normal.webp",
    "/images/h_roughness.webp",
  ]);

  _.each(
    [
      bodyTexture,
      eyesTexture,
      teethTexture,
      teethNormalTexture,
      bodySpecularTexture,
      bodyRoughnessTexture,
      bodyNormalTexture,
      tshirtDiffuseTexture,
      tshirtNormalTexture,
      tshirtRoughnessTexture,
      hairAlphaTexture,
      hairNormalTexture,
      hairRoughnessTexture,
    ],
    (t) => {
      t.encoding = sRGBEncoding;
      t.flipY = false;
    }
  );

  bodyNormalTexture.encoding = LinearEncoding;
  tshirtNormalTexture.encoding = LinearEncoding;
  teethNormalTexture.encoding = LinearEncoding;
  hairNormalTexture.encoding = LinearEncoding;

  gltf.scene.traverse((node) => {
    if (
      node.type === "Mesh" ||
      node.type === "LineSegments" ||
      node.type === "SkinnedMesh"
    ) {
      node.castShadow = true;
      node.receiveShadow = true;
      node.frustumCulled = false;

      if (node.name.includes("Wolf3D_Avatar")) {
        //node.castShadow = true;
        //node.receiveShadow = true;

        //node.material = new MeshPhysicalMaterial();
        //node.material.map = bodyTexture;
        // node.material.shininess = 60;
        //node.material.roughness = 1.7;

        // node.material.specularMap = bodySpecularTexture;
        //node.material.roughnessMap = bodyRoughnessTexture;
        //node.material.normalMap = bodyNormalTexture;
        //node.material.normalScale = new Vector2(0.6, 0.6);

        morphTargetDictionaryBody = node.morphTargetDictionary;

        //node.material.envMapIntensity = 0.8;
        // node.material.visible = false;
      }

      if (node.name.includes("Eyes")) {
        node.material = new MeshStandardMaterial();
        node.material.map = eyesTexture;
        // node.material.shininess = 100;
        node.material.roughness = 0.1;
        node.material.envMapIntensity = 0.5;
      }

      if (node.name.includes("Brows")) {
        node.material = new LineBasicMaterial({ color: 0x000000 });
        node.material.linewidth = 1;
        node.material.opacity = 0.5;
        node.material.transparent = true;
        node.visible = false;
      }

      if (node.name.includes("Teeth")) {
        node.receiveShadow = true;
        node.castShadow = true;
        node.material = new MeshStandardMaterial();
        node.material.roughness = 0.1;
        node.material.map = teethTexture;
        node.material.normalMap = teethNormalTexture;

        node.material.envMapIntensity = 0.7;
      }

      if (node.name.includes("Hair")) {
        node.material = new MeshStandardMaterial();
        node.material.map = hairTexture;
        node.material.alphaMap = hairAlphaTexture;
        node.material.normalMap = hairNormalTexture;
        node.material.roughnessMap = hairRoughnessTexture;

        node.material.transparent = true;
        node.material.depthWrite = false;
        node.material.side = 2;
        node.material.color.setHex(0x000000);

        node.material.envMapIntensity = 0.3;
      }

      if (node.name.includes("TSHIRT")) {
        node.material = new MeshStandardMaterial();

        node.material.map = tshirtDiffuseTexture;
        node.material.roughnessMap = tshirtRoughnessTexture;
        node.material.normalMap = tshirtNormalTexture;
        node.material.color.setHex(0xffffff);

        node.material.envMapIntensity = 0.5;
      }

      if (node.name.includes("TeethLower")) {
        morphTargetDictionaryLowerTeeth = node.morphTargetDictionary;
      }
    }
  });

  const [clips, setClips] = useState([]);
  const mixer = useMemo(() => new THREE.AnimationMixer(gltf.scene), []);

  useEffect(() => {
    if (mixerRef) {
      mixerRef.current = mixer;
    }
  }, [mixer]);

  useEffect(() => {
    if (speak === false) {
      // Stop the recognition if it exists
      if (recognitionRef && recognitionRef.current) {
        recognitionRef.current.stop();
      }
      return;
    }

    makeSpeech(text)
      .then((response) => {
        let { blendData, filename } = response.data;

        let newClips = [
          createAnimation(
            blendData,
            morphTargetDictionaryBody,
            "Wolf3D_Avatar"
          ),
        ];

        filename = host + filename;

        setClips(newClips);
        setAudioSource(filename);
      })
      .catch((err) => {
        console.error(err);
        setSpeak(false);
      });
  }, [speak]);

  let idleFbx = useFBX("/idle15.fbx");
  let { clips: idleClips } = useAnimations(idleFbx.animations);

  idleClips[0].tracks = _.filter(idleClips[0].tracks, (track) => {
    return (
      track.name.includes("Head") ||
      track.name.includes("Neck") ||
      track.name.includes("Spine2")
    );
  });

  idleClips[0].tracks = _.map(idleClips[0].tracks, (track) => {
    if (track.name.includes("Head")) {
      track.name = "head.quaternion";
    }

    if (track.name.includes("Neck")) {
      track.name = "neck.quaternion";
    }

    if (track.name.includes("Spine")) {
      track.name = "spine2.quaternion";
    }

    return track;
  });

  useEffect(() => {
    let idleClipAction = mixer.clipAction(idleClips[0]);
    idleClipAction.play();

    let blinkClip = createAnimation(
      blinkData,
      morphTargetDictionaryBody,
      "HG_Body"
    );
    let blinkAction = mixer.clipAction(blinkClip);
    blinkAction.play();
  }, []);

  // Play animation clips when available
  useEffect(() => {
    if (playing === false) return;

    _.each(clips, (clip) => {
      let clipAction = mixer.clipAction(clip);
      clipAction.setLoop(THREE.LoopOnce);
      clipAction.play();
    });
  }, [playing]);

  useFrame((state, delta) => {
    mixer.update(delta);
  });

  return (
    <group name="avatar">
      <primitive object={gltf.scene} dispose={null} />
    </group>
  );
}

function makeSpeech(text) {
  return axios.post(host + "/talk", { text });
}

const STYLES = {
  area: {
    position: "absolute",
    top: "7rem",
    right: "10px",
    width: "40%",
    zIndex: 500,
  },
  text: {
    margin: "0px",
    width: "300px",
    padding: "5px",
    background: "none",
    color: "#ffffff",
    fontSize: "1.2em",
    border: "none",
  },
  speak: {
    padding: "10px",
    marginTop: "5px",
    display: "block",
    color: "#FFFFFF",
    background: "#222222",
    border: "None",
  },
  stop: {
    padding: "10px",
    marginTop: "5px",
    display: "block",
    color: "#FFFFFF",
    background: "red",
    border: "None",
    fontSize: "1em",
  },
  speakDisabled: {
    padding: "10px",
    marginTop: "5px",
    display: "block",
    color: "#FFFFFF",
    background: "#808080 !important", // Gray background for disabled state
    border: "None",
    cursor: "not-allowed", // Changes cursor to indicate disabled state
  },
  area2: { position: "absolute", top: "5px", right: "15px", zIndex: 500 },
  label: { color: "#777777", fontSize: "0.8em" },
};

function App() {
  const audioPlayer = useRef();
  const timerRef = useRef(null);
  const recognitionRef = useRef(null);
  const mixerRef = useRef(null); // Add this to store mixer reference

  const [speak, setSpeak] = useState(false);
  const [text, setText] = useState("Hey I'm vOne. How may i help you?");
  const [audioSource, setAudioSource] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [listen, setListen] = useState(false);
  const [isRecognitionActive, setIsRecognitionActive] = useState(false);

  const [queryParams, setQueryParams] = useState({});
  const [responseData, setResponseData] = useState(null);
  const [stop, setStop] = useState(false);

  const [isStopped, setIsStopped] = useState(false); // Add this state

  const sessionTimerRef = useRef(null);
  const [sessionTime, setSessionTime] = useState(30 * 60); // 30 minutes in seconds
  const [isSessionActive, setIsSessionActive] = useState(false);
  const [hasExplainBeenClicked, setHasExplainBeenClicked] = useState(false);
  const [isFirstExplainDone, setIsFirstExplainDone] = useState(false);
  const [isExplainDisabled, setIsExplainDisabled] = useState(false);
  const [sessionId, setSessionId] = useState(null);

  useEffect(() => {
    return () => {
      if (sessionTimerRef.current) {
        clearInterval(sessionTimerRef.current);
      }
    };
  }, []);

  const startSessionTimer = () => {
    if (!isSessionActive) {
      setIsSessionActive(true);
      sessionTimerRef.current = setInterval(() => {
        setSessionTime((prevTime) => {
          if (prevTime <= 1) {
            clearInterval(sessionTimerRef.current);
            handleSessionEnd();
            return 0;
          }
          return prevTime - 1;
        });
      }, 1000);
    }
  };

  const handleSessionEnd = async () => {
    try {
      await endSessionAPI(sessionId);
      console.log("Session ended successfully");
    } catch (error) {
      console.error("Session end API error:", error);
    }

    try {
      const result = await handleSendingReport();
      console.log("Session ended, email sent:", result);
      setResponseData("Session completed! Summary email has been sent.");
      setIsSessionActive(false);
      setSessionId(null); // Clear session ID
      handleStop();
      window.close(); // Standard approach
      window.open("", "_self").close(); // Alternative approach
      window.location.href = "about:blank"; // Fallback
    } catch (error) {
      console.error("Failed to send session summary:", error);
      setIsSessionActive(false);
      setSessionId(null); // Clear session ID
      handleStop();
      setResponseData(
        "Session ended but failed to send summary email. Please try manually."
      );
      window.close(); // Standard approach
      window.open("", "_self").close(); // Alternative approach
      window.location.href = "about:blank"; // Fallback
      // Still close the window even if email fails
    }
  };

  const formatTime = (timeInSeconds) => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = timeInSeconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
  };

  useEffect(() => {
    const params = queryString.parse(window.location.search);
    setQueryParams(params);
  }, []);

  // New function to handle button disabled states
  const isButtonDisabled = (buttonType) => {
    if (buttonType === "stop") return false;
    if (buttonType === "explain") return isExplainDisabled;
    if (buttonType === "qa") {
      if (!isFirstExplainDone) return true;
      return speak || playing || isRecognitionActive;
    }
    return speak || playing || isRecognitionActive;
  };

  // Modified stop function to properly stop audio
  function handleStop() {
    setIsStopped(true);
    //setSessionId(null); // Clear session ID

    // Stop audio playback
    if (audioPlayer.current && audioPlayer.current.audioEl.current) {
      audioPlayer.current.audioEl.current.pause();
      audioPlayer.current.audioEl.current.currentTime = 0;
    }

    // Stop speech recognition
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (error) {
        console.error("Error stopping recognition:", error);
      }
    }

    // Stop animation mixer
    if (mixerRef.current) {
      mixerRef.current.stopAllAction();
    }

    // Clear all states
    setAudioSource(null);
    setSpeak(false);
    setPlaying(false);
    setText("");
    setIsRecognitionActive(false);
    setListen(false);
    setResponseData(null);

    // Clear any pending timers
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    // Reset button states
    const testBtn = document.getElementById("testBtn");
    if (testBtn) {
      testBtn.disabled = false;
      testBtn.textContent = "Q & A";
    }

    // Add a small delay before allowing new interactions
    setTimeout(() => {
      setIsStopped(false);
    }, 300);
  }

  async function handleSendingReport() {
    const grade = queryParams.level || "grade-1";
    const email = queryParams.mail || "racherla.sridhar@gmail.com";
    const sname = queryParams.name || "Dhruv";
    const sessId = sessionId; // Add session ID to the request
    console.log("SESSID:::", sessId);
    try {
      const result = await sendEmail(grade, email, sname, sessId);
      return result;
    } catch (error) {
      console.error("Error sending email:", error);
      throw error;
    }
  }

  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (error) {
          console.error("Error stopping recognition on cleanup:", error);
        }
      }
      if (mixerRef.current) {
        mixerRef.current.stopAllAction();
      }
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      setIsRecognitionActive(false);
    };
  }, []);

  // Player event handlers
  const playerEnded = (e) => {
    if (!isStopped) {
      // Only proceed if not manually stopped
      setAudioSource(null);
      setSpeak(false);
      setPlaying(false);

      if (listen && !isRecognitionActive && text.includes("Can you spell")) {
        setMikeListen();
      }
    }
  };

  const playerReady = (e) => {
    if (!isStopped) {
      // Only play if not manually stopped
      audioPlayer.current.audioEl.current.play();
      setPlaying(true);
    }
  };

  function randomPhrase(phrases) {
    var number = Math.floor(Math.random() * phrases.length);
    return number;
  }

  async function endSessionAPI(sessionId) {
    try {
      const response = await axios.post(
        "https://amatutor.vplaysmart.com/end-session",
        {
          session_id: sessionId,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error ending session:", error);
      throw error;
    }
  }

  async function getGreetingMessageByGrade(grade) {
    try {
      const response = await axios.get(
        `https://amatutor.vplaysmart.com/greeting?grade=${grade}`
      );
      console.log("Response:", response.data);
      return response.data;
    } catch (error) {
      console.error("Error:", error);
      throw error;
    }
  }

  async function sendEmail(grade, mail, name, sessId) {
    try {
      const response = await axios.get(
        `https://amatutor.vplaysmart.com/meetsummary/generate-and-email-summary/conversation_log.txt?&email=${mail}&student=${name}&session_id=${sessId}`
      );
      console.log("Response:", response.data);
      return response.data;
    } catch (error) {
      console.error("Error:", error);
      throw error;
    }
  }

  async function getQuesitionResponse(grade, question, name) {
    console.log("GRADE::", grade);
    console.log("question::", question);
    console.log("name::", name);
    try {
      const response = await axios.post(
        "https://amatutor.vplaysmart.com/generate-response",
        {
          question: question,
          grade: grade,
          name: name,
          chat_history: [],
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      console.log("Response:", response.data);
      // Store the session ID from the response
      if (response.data.session_id) {
        setSessionId(response.data.session_id);
        console.log("Session ID received:", response.data.session_id);
      }

      return response.data;
    } catch (error) {
      console.error("Error:", error);
      throw error;
    }
  }

  async function getUserQuesitionResult(grade, question) {
    console.log("GRADE::", grade);
    console.log("question::", question);
    try {
      const response = await axios.post(
        "https://amatutor.vplaysmart.com/generate-response",
        {
          question: question,
          grade: grade,
          session_id: sessionId, // Include session ID in the request
          chat_history: [],
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      console.log("Response:", response.data.response);
      return response.data.response;
    } catch (error) {
      console.error("Error:", error);
      throw error;
    }
  }

  async function handleMikeListening() {
    if (!hasExplainBeenClicked) {
      setHasExplainBeenClicked(true);
      startSessionTimer();
    }

    // Disable the explain button
    setIsExplainDisabled(true);

    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    setIsRecognitionActive(false);

    const testBtn = document.getElementById("testBtn");
    if (testBtn) {
      testBtn.disabled = true;
      testBtn.textContent = "vOne Processing...";
    }

    let grade = 1;
    let quesition = "What is kinetic energy";
    if (queryParams.level == "grade1") {
      grade = 1;
    } else if (queryParams.level == "grade2") {
      grade = 2;
    } else if (queryParams.level == "grade3") {
      grade = 3;
    }
    let name = queryParams.name || "dhruv";
    try {
      const result = await getQuesitionResponse(grade, quesition, name);
      let resData = result.response;

      console.log("resData::::", resData);
      setSpeak(true);
      setText(resData);
      setResponseData(resData);
      setListen(false);
      if (testBtn) {
        testBtn.disabled = false;
        testBtn.textContent = "Q & A";
      }
      // Set first explain as done after successful response
      setIsFirstExplainDone(true);
    } catch (error) {
      console.error("Error getting answer:", error);
      setSpeak(true);
      setText("Sorry, there was an error. Please try again.");
    }
  }

  async function getGreetingMessage() {
    let grade = 1;
    if (queryParams.level == "grade1") {
      grade = 1;
    } else if (queryParams.level == "grade2") {
      grade = 2;
    } else if (queryParams.level == "grade3") {
      grade = 3;
    }
    const result = await getGreetingMessageByGrade(grade);
    let res = result?.greeting;
    setSpeak(true);
    setText(res);
    setListen(false);
    setResponseData(res);
  }

  async function getUserQueryResponse(quesition) {
    let grade = 1;
    if (queryParams.level == "grade1") {
      grade = 1;
    } else if (queryParams.level == "grade2") {
      grade = 2;
    } else if (queryParams.level == "grade3") {
      grade = 3;
    }
    const result = await getUserQuesitionResult(grade, quesition);
    return result;
  }

  function setMikeListen() {
    if (isRecognitionActive) return;

    // Check if we have a valid session
    if (!sessionId) {
      console.error("No active session ID found");
      setSpeak(true);
      setText("Please click 'Explain' first to start a new session.");
      return;
    }

    const recognition = new (window.webkitSpeechRecognition ||
      window.SpeechRecognition)();
    recognitionRef.current = recognition;

    recognition.continuous = true; // Changed to true for longer listening
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;
    recognition.lang = "en-US";

    // Configure longer timeout
    let lastSpeechTime = Date.now();
    let timeoutId = null;

    recognition.onstart = () => {
      setIsRecognitionActive(true);
      console.log("Speech recognition started");
      lastSpeechTime = Date.now();
    };

    recognition.onaudiostart = () => {
      lastSpeechTime = Date.now();
    };

    recognition.onresult = (event) => {
      lastSpeechTime = Date.now();
      const result = event.results[event.results.length - 1];

      if (result.isFinal) {
        const speechResult = result[0].transcript
          .toLowerCase()
          .replace("canyouspell", "");

        console.log("Speech result:", speechResult);

        getUserQueryResponse(speechResult).then((res) => {
          if (res) {
            recognition.stop();
            setSpeak(true);
            setText(res);
            setListen(false);
            setResponseData(res);
            const testBtn = document.getElementById("testBtn");
            if (testBtn) {
              testBtn.disabled = false;
              testBtn.textContent = "Next Question";
            }
          } else {
            setSpeak(true);
            setText("That's not correct. Try again!");
          }
        });
      }
    };

    recognition.onend = () => {
      console.log("Speech recognition ended");
      setIsRecognitionActive(false);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };

    recognition.onerror = (event) => {
      console.error("Speech recognition error:", event.error);
      if (event.error !== "no-speech") {
        setIsRecognitionActive(false);
        setSpeak(true);
        setText("There was an error with the microphone. Please try again.");

        const testBtn = document.getElementById("testBtn");
        if (testBtn) {
          testBtn.disabled = false;
          testBtn.textContent = "Try Again";
        }
      }
    };

    // Set up the timeout checker
    const checkTimeout = () => {
      const timeSinceLastSpeech = Date.now() - lastSpeechTime;
      if (timeSinceLastSpeech > 15000) {
        // 15 seconds timeout
        recognition.stop();
      } else {
        timeoutId = setTimeout(checkTimeout, 1000);
      }
    };

    try {
      recognition.start();
      timeoutId = setTimeout(checkTimeout, 1000);
    } catch (error) {
      console.error("Failed to start speech recognition:", error);
    }
  }
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      setIsRecognitionActive(false);
    };
  }, []);
  return (
    <div className="full">
      <div id="right-section" style={STYLES.area}>
        {/*<textarea rows={4} type="text" style={STYLES.text} value={text} onChange={(e) => setText(e.target.value.substring(0, 200))} />
        <button onClick={() => setSpeak(true)} style={STYLES.speak}> { speak? 'Running...': 'Speak' }</button>*/}
        {/* Only show timer if session is active */}
        {isSessionActive && (
          <div
            style={{
              position: "absolute",
              top: "-40px",
              right: "10px",
              color: sessionTime < 300 ? "#ff0000" : "#e43939",
              fontSize: "1.2em",
              fontWeight: "bold",
            }}
          >
            Session Time: {formatTime(sessionTime)}
          </div>
        )}
        <div className="button-sections">
          <button
            id="evone"
            onClick={handleMikeListening}
            style={
              isButtonDisabled("explain") ? STYLES.speakDisabled : STYLES.speak
            }
            disabled={isButtonDisabled("explain")}
          >
            {speak ? "Running..." : "Explain"}
          </button>
          &nbsp;&nbsp;
          <button
            id="testBtn"
            onClick={setMikeListen}
            style={STYLES.speak}
            disabled={isButtonDisabled("qa")}
          >
            {listen ? "vOne Processing..." : "Q & A"}
          </button>
          &nbsp;&nbsp;
          <button id="stopBtn" onClick={handleStop} style={STYLES.stop}>
            {stop ? "Stopping..." : "Stop"}
          </button>
          &nbsp;&nbsp;
          <button
            id="sendReport"
            onClick={handleSendingReport}
            style={STYLES.speak}
          >
            {stop ? "Sending..." : "Send Email"}
          </button>
          &nbsp;&nbsp;
          <button
            id="endSession"
            onClick={handleSessionEnd}
            style={STYLES.speak}
          >
            {stop ? "End Session" : "End Session"}
          </button>
        </div>
        {responseData && <div className="displayContent">{responseData}</div>}
      </div>

      <ReactAudioPlayer
        src={audioSource}
        ref={audioPlayer}
        onEnded={playerEnded}
        onCanPlayThrough={playerReady}
      />

      {/* <Stats /> */}
      <Canvas
        dpr={2}
        onCreated={(ctx) => {
          ctx.gl.physicallyCorrectLights = true;
        }}
      >
        <OrthographicCamera makeDefault zoom={1000} position={[0.4, 1.55, 1]} />

        {/* <OrbitControls
        target={[0, 1.65, 0]}
      /> */}

        <Suspense fallback={null}>
          <Environment
            background={false}
            files="/images/photo_studio_loft_hall_1k.hdr"
          />
        </Suspense>

        <Suspense fallback={null}>
          <Bg />
        </Suspense>

        <Suspense fallback={null}>
          <Avatar
            avatar_url="/model15.glb"
            speak={speak}
            setSpeak={setSpeak}
            text={text}
            setAudioSource={setAudioSource}
            playing={playing}
            mixerRef={mixerRef}
            recognitionRef={recognitionRef} //
          />
        </Suspense>
      </Canvas>
      <Loader dataInterpolation={(p) => `Loading... please wait`} />
    </div>
  );
}

function Bg() {
  const texture = useTexture("/images/bg1.webp");

  return (
    <mesh position={[0.3, 1.6, -2]} scale={[0.4, 0.4, 0.4]}>
      <planeBufferGeometry />
      <meshBasicMaterial map={texture} />
    </mesh>
  );
}

export default App;
